/** AppShell: the sidebar beside the page. From 1024 px the sidebar is a column that collapses to nothing; below, it is a drawer over a scrim. */
import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { cx } from "./cx";
import { TooltipProvider } from "./Tooltip";
import { DRAWER_OPENS } from "./overlay";

const DESKTOP = "(min-width: 1024px)";

function useDesktop(): boolean {
  const [desktop, setDesktop] = useState(() => window.matchMedia(DESKTOP).matches);
  useEffect(() => {
    const query = window.matchMedia(DESKTOP);
    const sync = () => setDesktop(query.matches);
    query.addEventListener("change", sync);
    return () => query.removeEventListener("change", sync);
  }, []);
  return desktop;
}

interface ShellContext {
  /** True when no sidebar is on screen, so the top bar shows the button that brings it back. */
  sidebarHidden: boolean;
  showSidebar: () => void;
}

interface SidebarSlot {
  /** Hide this copy of the sidebar: collapse the column, or close the drawer. */
  dismiss: () => void;
  /** The column fades its content out as it collapses; the drawer has nothing to add. */
  className?: string;
}

const Shell = createContext<ShellContext | null>(null);
const Slot = createContext<SidebarSlot | null>(null);

function need<T>(value: T | null): T {
  if (!value) throw new Error("Kit shell parts must be rendered inside <AppShell>.");
  return value;
}

export const useShell = () => need(useContext(Shell));
export const useSidebarSlot = () => need(useContext(Slot));

interface AppShellProps {
  /** Rendered twice (the column and the drawer), so it must keep its own state. */
  sidebar: ReactNode;
  topBar: ReactNode;
  children: ReactNode;
}

export function AppShell({ sidebar, topBar, children }: AppShellProps) {
  const desktop = useDesktop();
  const [collapsed, setCollapsed] = useState(false);
  const [drawer, setDrawer] = useState(false);

  useEffect(() => {
    if (!drawer) return;
    const onKey = (event: KeyboardEvent) => event.key === "Escape" && setDrawer(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [drawer]);

  useEffect(() => {
    if (desktop) setDrawer(false);
  }, [desktop]);

  // One panel at a time: the drawer opening closes an open sheet (Sheet listens), as any close of it does.
  useEffect(() => {
    if (drawer) document.dispatchEvent(new Event(DRAWER_OPENS));
  }, [drawer]);

  return (
    <TooltipProvider>
      <div className="flex min-h-dvh w-full bg-background">
        <div
          className={cx("sticky top-0 hidden h-dvh shrink-0 overflow-hidden transition-[width] duration-200 ease-out-smooth lg:block", collapsed ? "w-0" : "w-sidebar")}
          inert={collapsed || undefined}
        >
          <Slot.Provider value={{ dismiss: () => setCollapsed(true), className: cx("transition-opacity duration-200", collapsed && "opacity-0") }}>{sidebar}</Slot.Provider>
        </div>
        <div className={cx("fixed inset-0 z-40 lg:hidden", drawer ? "pointer-events-auto" : "pointer-events-none")} aria-hidden={!drawer}>
          {/* The scrim is a pointer shortcut; Escape and the sidebar's own button close the drawer for everyone else. */}
          {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
          <div
            className={cx("absolute inset-0 bg-black/20 backdrop-blur-[2px] transition-opacity duration-200", drawer ? "opacity-100" : "opacity-0")}
            onClick={() => setDrawer(false)}
          />
          <div
            role="dialog"
            aria-modal="true"
            aria-label="Navigation"
            inert={!drawer || undefined}
            className={cx(
              "absolute inset-y-0 left-0 h-dvh bg-background pb-[env(safe-area-inset-bottom)] shadow-drawer transition-transform duration-200 ease-out-smooth",
              drawer ? "translate-x-0" : "-translate-x-full",
            )}
          >
            <Slot.Provider value={{ dismiss: () => setDrawer(false) }}>{sidebar}</Slot.Provider>
          </div>
        </div>
        <main className="flex min-w-0 flex-1 flex-col bg-card px-3 shadow-frame lg:min-h-dvh">
          <Shell.Provider value={{ sidebarHidden: !desktop || collapsed, showSidebar: () => (desktop ? setCollapsed(false) : setDrawer(true)) }}>
            {topBar}
            {/* Page padding: 16 px, and 24 from 1280 px (SCALE-AND-COLOUR.md §6). */}
            <div className="flex w-full flex-col px-1 py-4 xl:px-3 xl:py-6">{children}</div>
          </Shell.Provider>
        </main>
      </div>
    </TooltipProvider>
  );
}
